import {type AutoColor, type IconName} from '@wandb/components';

import {capitalizeFirstLetter} from '../../../util/string';
import {type MemoryNoticeContent} from './MemoryNotice';
import {
  asString,
  callOutcome,
  entryList,
  type MemoryEntry,
  stringList,
} from './toolCall';

const MEMORY_APPLIED_STATUS = 'applied';

const READ_COLOR: AutoColor = 'moon';
const READ_ICON: IconName = 'notebook';
const WARNING_COLOR: AutoColor = 'sienna';
const WARNING_ICON: IconName = 'warning';

// NOTE: Mirrors MemoryOperation in `project_memory_tools.py`. A verb added
// there but not here renders through UNKNOWN_WRITE.
type MemoryOperation = 'create' | 'revise' | 'delete';

type WriteDisplay = {
  icon: IconName;
  color: AutoColor;
  // One form per sentence the verb has to fit — "Recorded X", "Recording X",
  // "Could not record X". All lowercase; the label capitalizes when it leads.
  applied: string;
  pending: string;
  refused: string;
};

const UNKNOWN_WRITE: WriteDisplay = {
  icon: 'bookmark',
  color: 'teal',
  applied: 'wrote',
  pending: 'writing to',
  refused: 'write to',
};

const WRITE_DISPLAY: Record<MemoryOperation, WriteDisplay> = {
  create: {
    icon: 'bookmark',
    color: 'teal',
    applied: 'recorded',
    pending: 'recording',
    refused: 'record',
  },
  revise: {
    icon: 'pencil-edit',
    color: 'teal',
    applied: 'updated',
    pending: 'updating',
    refused: 'update',
  },
  delete: {
    icon: 'delete',
    color: 'moon',
    applied: 'deleted',
    pending: 'deleting',
    refused: 'delete',
  },
};

/**
 * readNoticeContent describes a `read_project_memory` call. Before a result
 * arrives it labels the call from the names it asked for.
 */
export function readNoticeContent(
  args: Record<string, unknown>,
  result: unknown,
  isError: boolean
): MemoryNoticeContent {
  const {output, refusal, settled} = callOutcome(result, isError);
  if (refusal !== undefined) {
    return {
      icon: WARNING_ICON,
      color: WARNING_COLOR,
      label: 'Could not read project memory',
      refusal,
    };
  }

  const base = {icon: READ_ICON, color: READ_COLOR};
  if (!settled) {
    return {...base, label: readingLabel(stringList(args.names))};
  }
  if (
    output === null ||
    !Array.isArray(output.memories) ||
    !Array.isArray(output.missing)
  ) {
    return {
      icon: WARNING_ICON,
      color: WARNING_COLOR,
      label: 'Could not confirm reading project memory',
    };
  }

  const found = entryList(output?.memories);
  const missing = stringList(output?.missing);
  return {...base, label: readLabel(found, missing), memories: found, missing};
}

/**
 * writeNoticeContent describes a `write_project_memory` call. It prefers the
 * result's name over the argument's, since a rename reports the name the memory
 * holds afterwards.
 */
export function writeNoticeContent(
  args: Record<string, unknown>,
  result: unknown,
  isError: boolean
): MemoryNoticeContent {
  const {output, refusal, settled} = callOutcome(result, isError);
  const operation = asString(output?.operation) ?? asString(args.operation);
  const display = writeDisplay(operation);
  const name = asString(output?.name) ?? asString(args.name);
  const object = memoryPhrase(name);

  if (refusal !== undefined) {
    return {
      icon: WARNING_ICON,
      color: WARNING_COLOR,
      label: `Could not ${display.refused} ${object}`,
      refusal,
    };
  }

  if (!settled) {
    return {
      icon: display.icon,
      color: display.color,
      label: `${capitalizeFirstLetter(display.pending)} ${object}`,
    };
  }
  if (output?.status !== MEMORY_APPLIED_STATUS) {
    return {
      icon: WARNING_ICON,
      color: WARNING_COLOR,
      label: `Could not confirm ${display.pending} ${object}`,
    };
  }

  const memories =
    operation === 'create' || operation === 'revise'
      ? entryList([output])
      : undefined;
  return {
    icon: display.icon,
    color: display.color,
    label: `${capitalizeFirstLetter(display.applied)} ${object}`,
    memories,
  };
}

/**
 * writeDisplay picks the wording for a write's operation, falling back for one
 * the model got wrong. The key is model-supplied, so it has to be an own
 * property — a plain object answers `toString` from its prototype.
 */
function writeDisplay(operation: string | undefined): WriteDisplay {
  return operation !== undefined &&
    Object.prototype.hasOwnProperty.call(WRITE_DISPLAY, operation)
    ? WRITE_DISPLAY[operation as MemoryOperation]
    : UNKNOWN_WRITE;
}

function readingLabel(requested: string[]): string {
  if (requested.length === 1) {
    return `Reading ${memoryPhrase(requested[0])}`;
  }
  if (requested.length > 1) {
    return `Reading ${requested.length} project memories`;
  }
  return 'Reading project memory';
}

function readLabel(found: MemoryEntry[], missing: string[]): string {
  if (found.length === 1) {
    return `Read ${memoryPhrase(found[0].name)}`;
  }
  if (found.length > 1) {
    return `Read ${found.length} project memories`;
  }
  if (missing.length === 1) {
    return `Could not find ${memoryPhrase(missing[0])}`;
  }
  if (missing.length > 1) {
    return `Could not find ${missing.length} project memories`;
  }
  return 'Read project memory';
}

function memoryPhrase(name: string | undefined): string {
  return name ? `the project memory ${name}` : 'a project memory';
}
